import FormInput from '../FormInput';
import TextInput from '../textInput/TextInput';
import SegmentedTextInput from '../segmentedTextInput/SegmentedTextInput';
import SelectInput from '../selectInput/SelectInput';

import type { BankDetailErrors } from './useBankDetailsForm';
import type { TFunction } from 'i18next';
import type { SelectOption } from '../selectInput/SelectInput';

export const accountHolderNameKey = 'accountHolderName';
export const sortCodeKey = 'sortCode';
export const accountNumberKey = 'accountNumber';
export const paymentDayKey = 'paymentDay';

interface BankDetails {
    accountHolderName: string;
    sortCode: { key: string; value: string }[];
    accountNumber: string;
    paymentDay: string;
    accountType?: string;
    businessPartnerId?: number;
    bankName?: string;
}

export interface BankDetailsFormProps {
    bankDetails: BankDetails;
    bankDetailErrors: BankDetailErrors;
    daysOptions: SelectOption[];
    t: TFunction;
    onChange: (e: React.ChangeEvent<HTMLInputElement>, key: string) => void;
    onSortCodeChange: (sortCode: { key: string; value: string }[]) => void;
    onPaymentDayChange: (paymentDay: string) => void;
    showPaymentDay?: boolean;
}

const fieldConfigs: Array<{
    key: string;
    label: (t: TFunction) => string;
    render: (props: BankDetailsFormProps) => JSX.Element;
    error?: (errors: BankDetailErrors) => string | undefined;
}> = [
    {
        key: accountHolderNameKey,
        label: (t) => t('account_holder_name'),
        render: (props) => (
            <TextInput
                name={accountHolderNameKey}
                dataKey={accountHolderNameKey}
                value={props.bankDetails[accountHolderNameKey]}
                placeholder={props.t('account_holder_name')}
                ariaDescribeLink={accountHolderNameKey}
                onChange={props.onChange}
            />
        ),
        error: undefined
    },
    {
        key: sortCodeKey,
        label: (t) => t('sort_code'),
        render: (props) => (
            <SegmentedTextInput
                name={sortCodeKey}
                dataKey={sortCodeKey}
                values={props.bankDetails[sortCodeKey]}
                placeholder='00'
                maxLengthWidth
                maxLength={2}
                numeric
                ariaDescribeLink={sortCodeKey}
                onChange={props.onSortCodeChange}
            />
        ),
        error: undefined
    },
    {
        key: accountNumberKey,
        label: (t) => t('account_number'),
        render: (props) => (
            <TextInput
                name={accountNumberKey}
                dataKey={accountNumberKey}
                value={props.bankDetails[accountNumberKey]}
                placeholder={props.t('account_number')}
                ariaDescribeLink={accountNumberKey}
                onChange={props.onChange}
            />
        ),
        error: (errors) => errors?.accountNumber
    }
];

const BankDetailsForm = (props: BankDetailsFormProps) => {
    const {
        bankDetailErrors,
        t,
        showPaymentDay,
        daysOptions,
        onPaymentDayChange,
        bankDetails
    } = props;

    return (
        <>
            {fieldConfigs.map((field) => (
                <FormInput
                    key={field.key}
                    id={field.key}
                    label={field.label(t)}
                    errorText={
                        field.error ? field.error(bankDetailErrors) : undefined
                    }>
                    {field.render(props)}
                </FormInput>
            ))}
            {showPaymentDay && (
                <FormInput
                    id={paymentDayKey}
                    label={t('preferred_payment_date')}>
                    <SelectInput
                        name={paymentDayKey}
                        value={bankDetails[paymentDayKey]}
                        options={daysOptions}
                        defaultText={t('select_default')}
                        onChange={onPaymentDayChange}
                    />
                    <p className='small mb-0 text-body-secondary'>
                        <em className='small'>{t('payment_date_notice')}</em>
                    </p>
                    {bankDetailErrors.bankValidation && (
                        <div className='error-style'>
                            {bankDetailErrors.bankValidation}
                        </div>
                    )}
                </FormInput>
            )}
        </>
    );
};

export default BankDetailsForm;
